import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCustomerAuth, customerApi } from "@/lib/customerAuth";
import {
    CalendarDays, Users, Tag, BedDouble, Ruler, Eye, ArrowRight, ArrowLeft,
    Check, ChevronDown, Info, Shield, XCircle, Baby, Sparkles
} from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api } from "@/lib/data";
import RoomPreviewMedia from "@/components/RoomPreviewMedia";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

/* ---------------- helpers ---------------- */
const todayPlus = (d) => {
    const x = new Date();
    x.setDate(x.getDate() + d);
    return x.toISOString().slice(0, 10);
};
const fmtDate = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const usd = (n) => `$${n.toFixed(0)}`;
const usd2 = (n) => `$${n.toFixed(2)}`;

const TAX_RATE = 0.15;                    // 15 % tax
const RESORT_FEE_PER_NIGHT = 15;          // $15 nightly resort fee
const BEST_VALUE_DISCOUNT = 0.12;         // 12 % off flexible rate
const PROMO_CODES = { SERENITY10: 0.10 };

/** Compute both rate plans from the booking-engine flexible rate. */
function computeRates(basePrice, nights) {
    const n = Math.max(1, nights);
    const flexPerNight = basePrice;
    const bestPerNight = Math.round(basePrice * (1 - BEST_VALUE_DISCOUNT));

    const build = (perNight) => {
        const subtotal = perNight * n;
        const resortFee = RESORT_FEE_PER_NIGHT * n;
        const taxes = Math.round((subtotal + resortFee) * TAX_RATE);
        const total = subtotal + resortFee + taxes;
        return { perNight, subtotal, resortFee, taxes, total, nights: n };
    };
    return { flexible: build(flexPerNight), bestValue: build(bestPerNight) };
}

/* ---------------- Rate Card ---------------- */
function RateCard({ kind, rate, checkIn, savings, selected, onSelect }) {
    const cancellationDeadline = useMemo(() => {
        const d = new Date(checkIn + "T00:00:00");
        d.setDate(d.getDate() - 3);
        return d.toLocaleDateString("en-US", { month: "long", day: "numeric" });
    }, [checkIn]);
    const isFlex = kind === "flexible";
    return (
        <div
            data-testid={`rate-${kind}`}
            className={`rounded-2xl p-5 border transition-all ${
                selected ? "border-gold shadow-xl shadow-gold/20 bg-gold/5" : "border-navy/10 bg-white hover:border-gold/60"
            }`}
        >
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-gold flex items-center gap-1.5">
                        {isFlex ? <Shield size={11} /> : <Sparkles size={11} />}
                        {isFlex ? "Flexible Rate" : "Best Value"}
                    </div>
                    <p className="mt-1 text-xs text-navy/70">
                        {isFlex
                            ? `Free cancellation until ${cancellationDeadline}`
                            : "Non-refundable · pay in full at booking"}
                    </p>
                </div>
                {!isFlex && savings > 0 && (
                    <span className="text-[10px] uppercase tracking-[0.18em] bg-jade/15 text-jade px-2.5 py-1 rounded-full">Save ${savings}</span>
                )}
            </div>

            <div className="mt-4 flex items-end justify-between">
                <div>
                    <div className="font-display text-3xl text-navy leading-none">{usd(rate.perNight)}</div>
                    <div className="text-[11px] text-navy/60 mt-1">per night · USD</div>
                </div>
                <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-navy/50">Total w/ taxes</div>
                    <div className="font-display text-2xl text-gold leading-none mt-1">{usd(rate.total)}</div>
                </div>
            </div>

            <button
                type="button"
                data-testid={`select-rate-${kind}`}
                onClick={onSelect}
                className={`mt-4 w-full py-3 rounded-full text-[11px] uppercase tracking-[0.16em] transition-colors ${
                    selected ? "bg-navy text-ivory" : "btn-gold"
                }`}
            >
                {selected ? "Selected" : isFlex ? "Select Flexible Rate" : "Select Best Value"}
            </button>
        </div>
    );
}

/* ---------------- Room Row ---------------- */
function RoomRow({ room, checkIn, nights, selection, onSelectRate, residentConfig }) {
    const [showBreakdown, setShowBreakdown] = useState(false);
    const rates = useMemo(() => computeRates(room.price_per_night, nights), [room.price_per_night, nights]);
    const savings = rates.flexible.perNight - rates.bestValue.perNight;
    const isSelectedFlex = selection?.slug === room.slug && selection.kind === "flexible";
    const isSelectedBest = selection?.slug === room.slug && selection.kind === "bestValue";
    const isSelectedResident = selection?.slug === room.slug && selection.kind === "resident";
    // Resident rate — derived per-room (room override falls back to global discount)
    const residentPct = residentConfig?.active
        ? ((residentConfig.room_overrides || {})[room.slug] || residentConfig.discount_pct || 0)
        : 0;
    const residentRate = useMemo(() => {
        if (!residentPct) return null;
        const t = Math.round(rates.flexible.total * (1 - residentPct / 100));
        const pn = Math.round(rates.flexible.perNight * (1 - residentPct / 100));
        return { total: t, perNight: pn, subtotal: rates.flexible.subtotal, resortFee: rates.flexible.resortFee, taxes: rates.flexible.taxes };
    }, [residentPct, rates]);

    return (
        <article data-testid={`booking-room-${room.slug}`} className="bg-white rounded-2xl border border-navy/10 overflow-hidden shadow-sm">
            <div className="grid lg:grid-cols-12 gap-0">
                {/* MEDIA (left) */}
                <div className="lg:col-span-4">
                    <div className="relative aspect-[4/3] lg:aspect-auto lg:h-full min-h-[260px]">
                        <RoomPreviewMedia image={room.hero_image} video={room.hero_video} alt={room.name} className="w-full h-full" />
                    </div>
                </div>

                {/* DETAILS (center) */}
                <div className="lg:col-span-5 p-6 lg:p-8 border-t lg:border-t-0 lg:border-l border-navy/10">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-gold">{room.view}</div>
                    <h3 className="font-display text-2xl md:text-3xl text-navy mt-2">{room.name}</h3>
                    <p className="font-serif-italic text-jade mt-1">{room.tagline}</p>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-5 border-t border-navy/10 text-navy">
                        <div><div className="text-[10px] uppercase tracking-[0.18em] text-navy/50 flex items-center gap-1"><Users size={11} /> Sleeps</div><div className="mt-1 text-sm font-medium">{room.occupancy}</div></div>
                        <div><div className="text-[10px] uppercase tracking-[0.18em] text-navy/50 flex items-center gap-1"><BedDouble size={11} /> Beds</div><div className="mt-1 text-sm font-medium">{room.beds}</div></div>
                        <div><div className="text-[10px] uppercase tracking-[0.18em] text-navy/50 flex items-center gap-1"><Ruler size={11} /> Size</div><div className="mt-1 text-sm font-medium">{room.size_sqft} sq ft</div></div>
                        <div><div className="text-[10px] uppercase tracking-[0.18em] text-navy/50 flex items-center gap-1"><Eye size={11} /> View</div><div className="mt-1 text-sm font-medium truncate">{room.view}</div></div>
                    </div>

                    <p className="text-sm text-navy/75 mt-4 leading-relaxed line-clamp-3">{room.description}</p>

                    {room.features?.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                            {room.features.slice(0, 5).map((f) => (
                                <span key={f} className="text-[10px] uppercase tracking-[0.14em] bg-sand/50 text-navy/80 px-2.5 py-1 rounded-full">{f}</span>
                            ))}
                        </div>
                    )}

                    <div className="mt-5 flex items-center gap-4 text-[11px] text-navy/60">
                        <span className="inline-flex items-center gap-1.5"><Shield size={11} className="text-jade" /> Taxes 15% + resort fee included</span>
                    </div>

                    <div className="mt-4 flex items-center gap-3">
                        <Link to={`/rooms/${room.slug}`} data-testid={`view-room-${room.slug}`} className="text-[11px] uppercase tracking-[0.2em] text-navy hover:text-gold inline-flex items-center gap-1.5">
                            View Room Details <ArrowRight size={12} />
                        </Link>
                    </div>
                </div>

                {/* RATES (right) */}
                <div className="lg:col-span-3 p-6 lg:p-8 bg-ivory border-t lg:border-t-0 lg:border-l border-navy/10 space-y-3">
                    <RateCard
                        kind="flexible"
                        rate={rates.flexible}
                        checkIn={checkIn}
                        savings={0}
                        selected={isSelectedFlex}
                        onSelect={() => onSelectRate(room, "flexible", rates.flexible)}
                    />
                    <RateCard
                        kind="bestValue"
                        rate={rates.bestValue}
                        checkIn={checkIn}
                        savings={savings * Math.max(1, nights)}
                        selected={isSelectedBest}
                        onSelect={() => onSelectRate(room, "bestValue", rates.bestValue)}
                    />
                    {residentRate && (
                        <div
                            data-testid={`rate-resident-${room.slug}`}
                            className={`rounded-2xl p-5 border transition-all ${isSelectedResident ? "border-jade shadow-xl shadow-jade/20 bg-jade/5" : "border-jade/40 bg-white hover:border-jade"}`}
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-jade flex items-center gap-1.5">
                                        <span className="w-1.5 h-1.5 rounded-full bg-jade" /> Jamaican Resident Rate
                                    </div>
                                    <p className="mt-1 text-xs text-navy/70">Verification required · {residentPct}% off standard</p>
                                </div>
                                <span className="text-[10px] uppercase tracking-[0.18em] bg-jade/15 text-jade px-2.5 py-1 rounded-full">-{residentPct}%</span>
                            </div>
                            <div className="mt-4 flex items-end justify-between">
                                <div>
                                    <div className="font-display text-3xl text-navy leading-none">{usd(residentRate.perNight)}</div>
                                    <div className="text-[11px] text-navy/60 mt-1">per night · USD</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-navy/50">Total w/ taxes</div>
                                    <div className="font-display text-2xl text-jade leading-none mt-1">{usd(residentRate.total)}</div>
                                </div>
                            </div>
                            <button
                                type="button"
                                data-testid={`select-rate-resident-${room.slug}`}
                                onClick={() => onSelectRate(room, "resident", residentRate)}
                                className={`mt-4 w-full py-3 rounded-full text-[11px] uppercase tracking-[0.16em] transition-colors ${isSelectedResident ? "bg-navy text-ivory" : "bg-jade text-ivory hover:bg-jade/90"}`}
                            >
                                {isSelectedResident ? "Selected" : "Select Resident Rate"}
                            </button>
                            <p className="mt-2 text-[10px] text-navy/50 text-center leading-relaxed">Subject to verification within 24h of booking. If verification fails the booking reverts to the standard rate.</p>
                        </div>
                    )}
                    <button
                        type="button"
                        data-testid={`breakdown-${room.slug}`}
                        onClick={() => setShowBreakdown((s) => !s)}
                        className="w-full text-[10px] uppercase tracking-[0.2em] text-navy/60 hover:text-gold flex items-center justify-center gap-1"
                    >
                        <Info size={11} /> View price breakdown <ChevronDown size={11} className={`transition-transform ${showBreakdown ? "rotate-180" : ""}`} />
                    </button>
                    {showBreakdown && (
                        <div className="rounded-xl border border-navy/10 p-4 text-xs bg-white space-y-1.5">
                            <Row label={`Room · ${nights} night${nights === 1 ? "" : "s"}`} value={usd(rates.flexible.subtotal)} sub={`(flex)`} />
                            <Row label="Resort fee" value={usd(rates.flexible.resortFee)} />
                            <Row label="Taxes (15%)" value={usd(rates.flexible.taxes)} />
                            <div className="border-t border-navy/10 pt-1.5 mt-1.5" />
                            <Row label="Flexible total" value={usd(rates.flexible.total)} strong />
                            <Row label="Best Value total" value={usd(rates.bestValue.total)} strong accent />
                        </div>
                    )}
                </div>
            </div>
        </article>
    );
}

function Row({ label, value, sub, strong, accent }) {
    return (
        <div className="flex items-center justify-between">
            <span className={`${strong ? "text-navy" : "text-navy/60"} ${accent ? "text-gold" : ""}`}>{label} {sub && <span className="text-navy/40">{sub}</span>}</span>
            <span className={`${strong ? "font-display text-navy" : "text-navy/70"} ${accent ? "text-gold" : ""}`}>{value}</span>
        </div>
    );
}

/* ---------------- Stepper ---------------- */
function Stepper({ step }) {
    const steps = [
        { n: 1, label: "Select Room" },
        { n: 2, label: "Guest Details" },
        { n: 3, label: "Review & Payment" },
        { n: 4, label: "Confirmation" },
    ];
    return (
        <div data-testid="booking-stepper" className="flex items-center gap-2 md:gap-4 flex-wrap">
            {steps.map((s, i) => {
                const active = s.n === step;
                const done = s.n < step;
                return (
                    <div key={s.n} className="flex items-center gap-2 md:gap-4">
                        <div className="flex items-center gap-2">
                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-medium ${
                                active ? "bg-gold text-navy" : done ? "bg-jade text-ivory" : "bg-navy/10 text-navy/60"
                            }`}>
                                {done ? <Check size={13} /> : s.n}
                            </span>
                            <span className={`text-[11px] uppercase tracking-[0.18em] ${active ? "text-navy" : done ? "text-jade" : "text-navy/50"}`}>
                                {s.label}
                            </span>
                        </div>
                        {i < steps.length - 1 && <span className="hidden md:inline-block w-8 h-[1px] bg-navy/20" />}
                    </div>
                );
            })}
        </div>
    );
}

/* ---------------- MAIN PAGE ---------------- */
export default function Booking() {
    const [params] = useSearchParams();
    const navigate = useNavigate();

    const [rooms, setRooms] = useState([]);
    const [residentConfig, setResidentConfig] = useState(null);
    const [showResident, setShowResident] = useState(false);
    const [residentStatus, setResidentStatus] = useState(null); // { verified, verified_until, reuse_months }
    const [step, setStep] = useState(1);
    const [inquiryCache, setInquiryCache] = useState({ key: null, id: null });
    const [busy, setBusy] = useState(false);

    // Search bar state
    const [search, setSearch] = useState({
        check_in: params.get("check_in") || todayPlus(14),
        check_out: params.get("check_out") || todayPlus(19),
        adults: parseInt(params.get("guests") || params.get("adults") || "2", 10),
        children: parseInt(params.get("children") || "0", 10),
        infants: parseInt(params.get("infants") || "0", 10),
        rooms: 1,
        promo_code: params.get("promo_code") || "",
    });
    const preselectSlug = params.get("room_slug");

    // Selection: { slug, kind: "flexible"|"bestValue", rate }
    const [selection, setSelection] = useState(null);

    // Guest details
    const [guest, setGuest] = useState({
        first_name: "", last_name: "", email: "", phone: "",
        country: "United States", address: "", arrival_time: "15:00",
        special_requests: "", accessibility: "",
    });
    const [confirmation, setConfirmation] = useState(null);

    useEffect(() => {
        api.get("/rooms").then(({ data }) => setRooms(data)).catch(() => {});
        api.get("/resident-rate/config").then(({ data }) => setResidentConfig(data)).catch(() => {});
    }, []);

    // Prefill contact info for signed-in customers
    const { user: customer } = useCustomerAuth();
    useEffect(() => {
        if (!customer) { setResidentStatus(null); return; }
        setGuest((g) => ({
            ...g,
            first_name: g.first_name || (customer.name || "").split(" ")[0] || "",
            last_name: g.last_name || (customer.name || "").split(" ").slice(1).join(" ") || "",
            email: g.email || customer.email || "",
            phone: g.phone || customer.phone || "",
            country: g.country || customer.country || "United States",
            address: g.address || customer.address || "",
            special_requests: g.special_requests || customer.special_requests || "",
        }));
        // Reusable Verified Resident status
        customerApi.get("/customer/me/resident-status")
            .then(({ data }) => setResidentStatus(data))
            .catch(() => setResidentStatus(null));
    }, [customer]);

    // Auto-scroll to preselected room
    useEffect(() => {
        if (preselectSlug && rooms.length) {
            setTimeout(() => {
                const el = document.querySelector(`[data-testid="booking-room-${preselectSlug}"]`);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 300);
        }
    }, [preselectSlug, rooms]);

    const nights = useMemo(() => {
        const a = new Date(search.check_in), b = new Date(search.check_out);
        return Math.max(0, Math.round((b - a) / 86400000));
    }, [search.check_in, search.check_out]);

    const promoDiscountPct = PROMO_CODES[search.promo_code.trim().toUpperCase()] || 0;
    const selectedRoom = useMemo(() => rooms.find((r) => r.slug === selection?.slug), [rooms, selection]);
    const totalBeforeRedemption = selection ? Math.round(selection.rate.total * (1 - promoDiscountPct)) : 0;

    // ---- Cove Rewards redemption (customer signed in only) ----
    const [redeemPoints, setRedeemPoints] = useState(0);
    const availablePoints = customer?.loyalty?.available || 0;
    const redemptionRate = 100; // 100 pts = $1 (mirrors server LOYALTY_REDEMPTION_RATE)
    const minRedeemPoints = 500;
    const maxRedeemPoints = Math.min(availablePoints, totalBeforeRedemption * redemptionRate);
    // Round down to nearest 100 pts for a clean UI
    const clampedRedeemPoints = redeemPoints >= minRedeemPoints
        ? Math.min(redeemPoints, maxRedeemPoints) - (Math.min(redeemPoints, maxRedeemPoints) % 100)
        : 0;
    const loyaltyDiscountUsd = clampedRedeemPoints / redemptionRate;
    const finalTotal = Math.max(0.5, totalBeforeRedemption - loyaltyDiscountUsd);

    /* ---- Handlers ---- */
    const selectRate = (room, kind, rate) => {
        setSelection({ slug: room.slug, kind, rate });
        setStep(2);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const submitDetails = async (e) => {
        e.preventDefault();
        if (!guest.first_name || !guest.last_name || !guest.email) {
            toast.error("Please fill in name and email to continue.");
            return;
        }
        setStep(3);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // Persist the reservation as an inquiry (used by PayPal + reservation-only flows).
    // Cached per room/dates/email so a PayPal retry reuses the same inquiry + hold.
    const persistInquiryAndGetId = async () => {
        const key = `${selectedRoom.slug}|${search.check_in}|${search.check_out}|${guest.email}`;
        if (inquiryCache.key === key && inquiryCache.id) return inquiryCache.id;
        const { data: inq } = await api.post("/bookings/inquiry", {
            full_name: `${guest.first_name} ${guest.last_name}`,
            email: guest.email,
            phone: guest.phone,
            check_in: search.check_in,
            check_out: search.check_out,
            guests: search.adults + search.children,
            room_slug: selectedRoom.slug,
            message: [
                `Rate: ${selection.kind === "flexible" ? "Flexible" : "Best Value"} · ${usd(selection.rate.total)} total`,
                guest.country ? `Country: ${guest.country}` : "",
                guest.address ? `Address: ${guest.address}` : "",
                guest.arrival_time ? `Arrival: ${guest.arrival_time}` : "",
                guest.special_requests ? `Requests: ${guest.special_requests}` : "",
                guest.accessibility ? `Accessibility: ${guest.accessibility}` : "",
                search.promo_code ? `Promo: ${search.promo_code}` : "",
            ].filter(Boolean).join(" · "),
        });
        setInquiryCache({ key, id: inq.id });
        return inq.id;
    };

    // PayPal — createOrder callback (charges the FULL booking amount)
    const paypalCreateOrder = async () => {
        if (!selectedRoom || !selection) return "";
        try {
            const inquiry_id = await persistInquiryAndGetId();
            // $1 live-test override — append ?test=1 to the booking URL to trigger it
            const isTestMode = params.get("test") === "1";
            const body = isTestMode
                ? { package_id: "test", inquiry_id }
                : {
                    room_slug: selectedRoom.slug,
                    check_in: search.check_in,
                    check_out: search.check_out,
                    rate_kind: selection.kind,
                    promo_code: search.promo_code || null,
                    redeem_points: clampedRedeemPoints || 0,
                    inquiry_id,
                };
            const { data } = await api.post("/payments/paypal/create-order", {
                ...body,
                metadata: {
                    guest_name: `${guest.first_name} ${guest.last_name}`,
                    email: guest.email,
                    room: selectedRoom.name,
                    check_in: search.check_in,
                    check_out: search.check_out,
                    test_mode: isTestMode ? "yes" : "no",
                },
            });
            return data.order_id;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not start PayPal checkout. Please try again.");
            throw new Error("paypal-create-order-failed");
        }
    };

    // PayPal — onApprove callback: server-side capture
    const paypalOnApprove = async (data) => {
        try {
            const { data: cap } = await api.post("/payments/paypal/capture", { order_id: data.orderID });
            if (cap.status === "COMPLETED") {
                toast.success("Payment received. Your reservation is confirmed.");
                setConfirmation({
                    id: (data.orderID || "").slice(-8).toUpperCase(),
                    paypal_order_id: data.orderID,
                    guest_name: `${guest.first_name} ${guest.last_name}`,
                    room: selectedRoom.name,
                    check_in: search.check_in,
                    check_out: search.check_out,
                    nights,
                    guests: search.adults + search.children,
                    total: finalTotal,
                    rate_kind: selection.kind,
                    email: guest.email,
                    paid: true,
                    paid_amount: cap.captured_amount,
                });
                setStep(4);
                window.scrollTo({ top: 0, behavior: "smooth" });
            } else {
                toast.error(`Payment status: ${cap.status}. Please contact the resort.`);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Payment could not be captured. Please contact the resort.");
        }
    };

    /* ---------------- RENDER ---------------- */
    return (
        <div data-testid="booking-page" className="bg-ivory min-h-screen pb-24">
            {/* Sticky Search bar */}
            <div className="pt-28 md:pt-32" />
            <div data-testid="booking-search-bar" className="sticky top-20 md:top-24 z-30 bg-ivory/95 backdrop-blur-md border-y border-navy/10 shadow-sm">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
                    <label className="col-span-1">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60 flex items-center gap-1"><CalendarDays size={11} /> Arrival</span>
                        <input data-testid="search-checkin" type="date" value={search.check_in} onChange={(e) => setSearch((s) => ({ ...s, check_in: e.target.value }))} className="mt-1 w-full bg-transparent border-b border-navy/20 py-1.5 text-sm" />
                    </label>
                    <label className="col-span-1">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60 flex items-center gap-1"><CalendarDays size={11} /> Departure</span>
                        <input data-testid="search-checkout" type="date" value={search.check_out} onChange={(e) => setSearch((s) => ({ ...s, check_out: e.target.value }))} className="mt-1 w-full bg-transparent border-b border-navy/20 py-1.5 text-sm" />
                    </label>
                    <label className="col-span-1">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60 flex items-center gap-1"><Users size={11} /> Adults</span>
                        <select data-testid="search-adults" value={search.adults} onChange={(e) => setSearch((s) => ({ ...s, adults: +e.target.value }))} className="mt-1 w-full bg-transparent border-b border-navy/20 py-1.5 text-sm">
                            {[1,2,3,4,5,6].map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </label>
                    <label className="col-span-1">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60 flex items-center gap-1"><Baby size={11} /> Children</span>
                        <select data-testid="search-children" value={search.children} onChange={(e) => setSearch((s) => ({ ...s, children: +e.target.value }))} className="mt-1 w-full bg-transparent border-b border-navy/20 py-1.5 text-sm">
                            {[0,1,2,3,4].map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </label>
                    <label className="col-span-1">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60 flex items-center gap-1"><Tag size={11} /> Promo</span>
                        <input data-testid="search-promo" type="text" placeholder="Optional" value={search.promo_code} onChange={(e) => setSearch((s) => ({ ...s, promo_code: e.target.value }))} className="mt-1 w-full bg-transparent border-b border-navy/20 py-1.5 text-sm uppercase tracking-wider placeholder:normal-case placeholder:text-navy/40" />
                    </label>
                    <button
                        data-testid="search-update"
                        onClick={() => { setStep(1); setSelection(null); toast.success(`Showing rates for ${nights} night${nights === 1 ? "" : "s"}.`); }}
                        className="btn-gold rounded-full py-2.5"
                    >
                        Update Search
                    </button>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10">
                <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.28em] text-gold">Reservations</div>
                        <h1 className="font-display text-4xl md:text-5xl text-navy mt-2">Book Your Stay</h1>
                        <p className="text-sm text-navy/60 mt-1">{fmtDate(search.check_in)} → {fmtDate(search.check_out)} · {nights} night{nights === 1 ? "" : "s"} · {search.adults + search.children} guest{(search.adults + search.children) === 1 ? "" : "s"}</p>
                    </div>
                    <Stepper step={step} />
                </div>

                {/* STEP 1 · Select Room */}
                {step === 1 && (
                    <section data-testid="step-1" className="space-y-6">
                        {residentConfig?.active && (
                            <div data-testid="resident-optin" className="bg-white rounded-2xl border border-jade/30 p-5 flex items-start gap-4">
                                <label className="flex items-start gap-3 flex-1 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={showResident}
                                        onChange={(e) => setShowResident(e.target.checked)}
                                        data-testid="resident-optin-checkbox"
                                        className="mt-1 accent-jade w-4 h-4"
                                    />
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-jade flex items-center gap-1.5">
                                            <span className="w-1.5 h-1.5 rounded-full bg-jade" /> I'm a Jamaican resident
                                        </div>
                                        <p className="mt-1 text-sm text-navy/70">
                                            Show the <b>Jamaican Resident Rate</b> — {residentConfig.discount_pct}% off standard.
                                            {residentStatus?.verified ? (
                                                <span className="ml-1 text-jade font-medium">You're already verified — no document required for this booking.</span>
                                            ) : residentConfig.verification_flow === "verify_before" ? (
                                                <span className="ml-1"><b>Verify first, then pay:</b> upload a valid ID or proof-of-address document and wait for staff approval before completing payment.</span>
                                            ) : (
                                                <span className="ml-1">Subject to verification within {residentConfig.deadline_hours} hours of booking — you'll upload a valid ID or proof-of-address after checkout.</span>
                                            )}
                                        </p>
                                        {residentStatus?.verified && residentStatus?.verified_until && (
                                            <div data-testid="resident-verified-badge" className="mt-2 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] bg-jade/15 text-jade px-2.5 py-1 rounded-full">
                                                Verified Resident · valid until {new Date(residentStatus.verified_until).toLocaleDateString()}
                                            </div>
                                        )}
                                    </div>
                                </label>
                            </div>
                        )}
                        {rooms.map((r) => (
                            <RoomRow
                                key={r.id}
                                room={r}
                                checkIn={search.check_in}
                                nights={nights}
                                selection={selection}
                                onSelectRate={selectRate}
                                residentConfig={showResident ? residentConfig : null}
                            />
                        ))}
                    </section>
                )}

                {/* STEP 2 · Guest Details */}
                {step === 2 && selectedRoom && (
                    <section data-testid="step-2" className="grid lg:grid-cols-3 gap-8">
                        <form onSubmit={submitDetails} className="lg:col-span-2 space-y-4 bg-white rounded-2xl p-8 border border-navy/10">
                            <h2 className="font-display text-2xl text-navy">Your Details</h2>
                            <p className="text-sm text-navy/60">Tell us who's arriving. All fields marked with * are required.</p>
                            <div className="grid md:grid-cols-2 gap-4">
                                <Input required label="First name *" testId="guest-first-name" value={guest.first_name} onChange={(v) => setGuest({ ...guest, first_name: v })} />
                                <Input required label="Last name *" testId="guest-last-name" value={guest.last_name} onChange={(v) => setGuest({ ...guest, last_name: v })} />
                                <Input required type="email" label="Email *" testId="guest-email" value={guest.email} onChange={(v) => setGuest({ ...guest, email: v })} />
                                <Input label="Phone" testId="guest-phone" value={guest.phone} onChange={(v) => setGuest({ ...guest, phone: v })} />
                                <Input label="Country" testId="guest-country" value={guest.country} onChange={(v) => setGuest({ ...guest, country: v })} />
                                <Input label="Address" testId="guest-address" value={guest.address} onChange={(v) => setGuest({ ...guest, address: v })} />
                                <Input type="time" label="Estimated arrival" testId="guest-arrival" value={guest.arrival_time} onChange={(v) => setGuest({ ...guest, arrival_time: v })} />
                            </div>
                            <Input label="Special requests" testId="guest-requests" value={guest.special_requests} onChange={(v) => setGuest({ ...guest, special_requests: v })} textarea />
                            <Input label="Accessibility requests" testId="guest-accessibility" value={guest.accessibility} onChange={(v) => setGuest({ ...guest, accessibility: v })} textarea />
                            <div className="flex items-center justify-between mt-2">
                                <button type="button" onClick={() => setStep(1)} className="text-navy hover:text-gold text-[11px] uppercase tracking-[0.18em] inline-flex items-center gap-1"><ArrowLeft size={12} /> Back</button>
                                <button type="submit" data-testid="continue-to-payment" className="btn-gold px-8 py-3.5 rounded-full inline-flex items-center gap-2">Continue to Payment <ArrowRight size={14} /></button>
                            </div>
                        </form>
                        <Summary
                            room={selectedRoom}
                            selection={selection}
                            search={search}
                            nights={nights}
                            promoDiscountPct={promoDiscountPct}
                            finalTotal={finalTotal}
                        />
                    </section>
                )}

                {/* STEP 3 · Review & Payment */}
                {step === 3 && selectedRoom && (
                    <section data-testid="step-3" className="grid lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-6 bg-white rounded-2xl p-8 border border-navy/10">
                            <h2 className="font-display text-2xl text-navy">Review your reservation</h2>
                            <div className="grid md:grid-cols-2 gap-6 text-sm">
                                <Field label="Room" value={selectedRoom.name} />
                                <Field label="Rate" value={selection?.kind === "flexible" ? "Flexible · Free cancellation" : "Best Value · Non-refundable"} />
                                <Field label="Guest" value={`${guest.first_name} ${guest.last_name}`} />
                                <Field label="Email" value={guest.email} />
                                <Field label="Arrival" value={fmtDate(search.check_in)} />
                                <Field label="Departure" value={fmtDate(search.check_out)} />
                                <Field label="Nights" value={String(nights)} />
                                <Field label="Guests" value={`${search.adults} adult${search.adults > 1 ? "s" : ""}${search.children ? `, ${search.children} child${search.children > 1 ? "ren" : ""}` : ""}`} />
                            </div>
                            <div className="mt-6 pt-6 border-t border-navy/10 space-y-1.5 text-sm">
                                <Row label={`${selectedRoom.name} · ${nights} night${nights === 1 ? "" : "s"}`} value={usd(selection.rate.subtotal)} />
                                <Row label="Resort fee" value={usd(selection.rate.resortFee)} />
                                <Row label="Taxes (15%)" value={usd(selection.rate.taxes)} />
                                {promoDiscountPct > 0 && <Row label={`Promo · ${search.promo_code.toUpperCase()}`} value={`-${Math.round(promoDiscountPct * 100)}%`} accent />}
                                {clampedRedeemPoints > 0 && (
                                    <Row
                                        label={`Cove Rewards · ${clampedRedeemPoints.toLocaleString()} pts`}
                                        value={`-${usd(loyaltyDiscountUsd)}`}
                                        accent
                                    />
                                )}
                                <div className="border-t border-navy/10 pt-2 mt-2 flex items-center justify-between">
                                    <span className="text-[11px] uppercase tracking-[0.22em] text-navy/60">Final total</span>
                                    <span className="font-display text-3xl text-gold">{usd2(finalTotal)}</span>
                                </div>
                            </div>
                            {customer && availablePoints >= minRedeemPoints && (
                                <div data-testid="redemption-card" className="mt-6 rounded-xl border border-gold/40 bg-gradient-to-br from-gold/5 to-transparent p-5">
                                    <div className="flex items-start justify-between gap-4 flex-wrap">
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.22em] text-gold flex items-center gap-1.5">
                                                <span className="w-1.5 h-1.5 rounded-full bg-gold" /> Cove Rewards
                                            </div>
                                            <div className="mt-1 text-sm text-navy">
                                                You have <b>{availablePoints.toLocaleString()}</b> points · worth up to <b>${(availablePoints / 100).toFixed(0)}</b>
                                            </div>
                                            <div className="text-[11px] text-navy/60 mt-0.5">100 points = $1 · min. 500 points · applied against room charge</div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {[500, 1000, 2500, maxRedeemPoints].filter((v, i, a) => v >= minRedeemPoints && v <= maxRedeemPoints && a.indexOf(v) === i).map((v) => (
                                                <button
                                                    key={v}
                                                    type="button"
                                                    onClick={() => setRedeemPoints(v)}
                                                    data-testid={`redeem-${v}`}
                                                    className={`text-[11px] uppercase tracking-[0.14em] px-3 py-1.5 rounded-full border transition-colors ${clampedRedeemPoints === v ? "bg-navy text-ivory border-navy" : "text-navy border-navy/25 hover:border-gold"}`}
                                                >
                                                    {v.toLocaleString()}
                                                </button>
                                            ))}
                                            {clampedRedeemPoints > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => setRedeemPoints(0)}
                                                    data-testid="redeem-clear"
                                                    className="text-[11px] uppercase tracking-[0.14em] px-3 py-1.5 rounded-full text-red-600 hover:bg-red-50"
                                                >
                                                    Clear
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mt-4">
                                        <input
                                            type="range"
                                            min={0}
                                            max={maxRedeemPoints}
                                            step={100}
                                            value={Math.min(redeemPoints, maxRedeemPoints)}
                                            onChange={(e) => setRedeemPoints(Number(e.target.value))}
                                            data-testid="redeem-slider"
                                            className="w-full accent-gold"
                                        />
                                        <div className="mt-2 flex items-center justify-between text-[11px] text-navy/60">
                                            <span>Redeeming <b className="text-navy">{clampedRedeemPoints.toLocaleString()}</b> pts</span>
                                            <span>Discount: <b className="text-gold">${loyaltyDiscountUsd.toFixed(2)}</b></span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {!customer && (
                                <div className="mt-6 rounded-xl border border-navy/10 bg-navy/[0.03] p-4 text-[12px] text-navy/70 flex items-center justify-between gap-3 flex-wrap">
                                    <span><b className="text-gold">Cove Rewards:</b> Sign in to redeem earned points and save on this stay.</span>
                                    <Link to="/signin" className="text-[11px] uppercase tracking-[0.14em] px-3 py-1.5 rounded-full border border-navy/25 hover:bg-navy hover:text-ivory">Sign in</Link>
                                </div>
                            )}
                            <div className="mt-6 space-y-3 text-xs text-navy/70 leading-relaxed">
                                <p className="flex items-start gap-2"><Shield size={13} className="text-jade shrink-0 mt-0.5" /> {selection?.kind === "flexible" ? "Free cancellation until 3 days before arrival." : "This rate is non-refundable. Payment will be charged in full."}</p>
                                <p className="flex items-start gap-2"><Info size={13} className="text-jade shrink-0 mt-0.5" /> All reservations are paid in full online via PayPal. There is no deposit and no pay-at-check-in option. Flexible-rate refunds are honoured up to 3 days before arrival.</p>
                            </div>
                            <div className="mt-6 pt-6 border-t border-navy/10">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-navy/60 mb-3">Pay in full with PayPal</div>
                                {params.get("test") === "1" && (
                                    <div data-testid="test-mode-banner" className="mb-4 rounded-lg border border-gold/40 bg-gold/10 px-4 py-3 text-xs text-navy/85">
                                        <span className="font-bold text-gold uppercase tracking-[0.18em] text-[10px]">Live $1 Test Mode</span>
                                        <span className="ml-2">PayPal will charge <b>$1.00 USD</b> — remove <code>?test=1</code> from the URL for the real payment.</span>
                                    </div>
                                )}
                                {PAYPAL_CLIENT_ID ? (
                                    <PayPalScriptProvider options={{ "client-id": PAYPAL_CLIENT_ID, currency: "USD", intent: "capture" }}>
                                        <div data-testid="paypal-buttons-wrap">
                                            <PayPalButtons
                                                style={{ layout: "vertical", color: "gold", shape: "pill", label: "paypal" }}
                                                createOrder={paypalCreateOrder}
                                                onApprove={paypalOnApprove}
                                                onError={() => toast.error("PayPal ran into an error. Please try again.")}
                                                onCancel={() => toast("Payment cancelled — your reservation was not confirmed.")}
                                            />
                                        </div>
                                    </PayPalScriptProvider>
                                ) : (
                                    <p className="text-xs text-navy/60">PayPal is not configured. Please contact the resort.</p>
                                )}
                            </div>
                            <div className="flex items-center justify-between mt-4">
                                <button type="button" onClick={() => setStep(2)} className="text-navy hover:text-gold text-[11px] uppercase tracking-[0.18em] inline-flex items-center gap-1"><ArrowLeft size={12} /> Back</button>
                                <Link to={`/contact?subject=${encodeURIComponent(`Booking help — ${selectedRoom?.name || "Room"} · ${search.check_in} → ${search.check_out}`)}`} data-testid="need-help-contact" className="text-[11px] uppercase tracking-[0.18em] text-navy/70 hover:text-gold underline underline-offset-4 decoration-navy/20 hover:decoration-gold">
                                    Prefer to arrange differently? Reach out to us
                                </Link>
                            </div>
                        </div>
                        <Summary
                            room={selectedRoom}
                            selection={selection}
                            search={search}
                            nights={nights}
                            promoDiscountPct={promoDiscountPct}
                            finalTotal={finalTotal}
                        />
                    </section>
                )}

                {/* STEP 4 · Confirmation */}
                {step === 4 && confirmation && (
                    <section data-testid="step-4" className="max-w-3xl mx-auto">
                        {confirmation.paid && (
                            <div data-testid="payment-receipt-card" className="rounded-2xl overflow-hidden mb-8 shadow-lg border border-jade/20">
                                <div className="bg-gradient-to-br from-navy via-navy to-jade text-ivory p-8 md:p-10 relative">
                                    <div className="absolute top-0 right-0 w-40 h-40 bg-gold/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                                    <div className="relative">
                                        <div className="text-[10px] uppercase tracking-[0.32em] text-gold flex items-center gap-2">
                                            <Check size={14} /> Payment Received
                                        </div>
                                        <div className="mt-4 flex items-baseline gap-3 flex-wrap">
                                            <span className="font-display text-6xl md:text-7xl text-ivory leading-none">
                                                {confirmation.paid_amount != null ? `$${Number(confirmation.paid_amount).toFixed(2)}` : "Paid"}
                                            </span>
                                            <span className="font-serif-italic text-gold text-xl">USD via PayPal</span>
                                        </div>
                                        <p className="mt-4 text-ivory/80 text-sm max-w-md leading-relaxed">
                                            Thank you, {confirmation.guest_name}. Your Serenity Cove reservation is confirmed and paid in full. A receipt is on its way to <span className="text-ivory">{confirmation.email}</span>.
                                        </p>
                                        {confirmation.paypal_order_id && (
                                            <div className="mt-6 pt-6 border-t border-ivory/15">
                                                <div className="text-[10px] uppercase tracking-[0.22em] text-ivory/60 mb-2">PayPal Transaction ID</div>
                                                <div className="flex items-center gap-3 flex-wrap">
                                                    <code data-testid="paypal-txn-id" className="font-mono text-sm md:text-base text-gold bg-navy/40 border border-gold/25 rounded-lg px-4 py-2 select-all break-all">
                                                        {confirmation.paypal_order_id}
                                                    </code>
                                                    <button
                                                        type="button"
                                                        data-testid="copy-txn-id"
                                                        onClick={() => {
                                                            navigator.clipboard?.writeText(confirmation.paypal_order_id);
                                                            toast.success("Transaction ID copied");
                                                        }}
                                                        className="text-[10px] uppercase tracking-[0.18em] text-ivory/80 hover:text-gold border border-ivory/25 hover:border-gold/60 rounded-full px-4 py-2 transition-colors"
                                                    >
                                                        Copy
                                                    </button>
                                                </div>
                                                <p className="mt-3 text-[11px] text-ivory/55 leading-relaxed">
                                                    Save this ID or take a screenshot — you'll need it if you contact the resort about your payment.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="text-center bg-white rounded-2xl border border-navy/10 p-10 md:p-14">
                            {!confirmation.paid && (
                                <div className="inline-flex w-16 h-16 rounded-full bg-jade/15 text-jade items-center justify-center">
                                    <Check size={30} />
                                </div>
                            )}
                            <h2 className="font-display text-4xl text-navy mt-6">Reservation Confirmed</h2>
                            <p className="font-serif-italic text-jade text-xl mt-3">Thank you, {confirmation.guest_name}.</p>
                            {!confirmation.paid && <p className="mt-3 text-sm text-navy/70">A confirmation email is on its way to <span className="text-navy font-medium">{confirmation.email}</span>.</p>}
                            <div className="mt-8 grid grid-cols-2 gap-4 text-left">
                                <ConfBox label="Confirmation number" value={`SC-${confirmation.id}`} />
                                <ConfBox label="Room" value={confirmation.room} />
                                <ConfBox label="Arrival" value={fmtDate(confirmation.check_in)} />
                                <ConfBox label="Departure" value={fmtDate(confirmation.check_out)} />
                                <ConfBox label="Nights" value={String(confirmation.nights)} />
                                <ConfBox label="Guests" value={String(confirmation.guests)} />
                                <ConfBox label="Stay total" value={usd2(confirmation.total)} />
                                <ConfBox label="Balance due at arrival" value={confirmation.paid && confirmation.paid_amount != null ? usd2(Math.max(0, confirmation.total - Number(confirmation.paid_amount))) : "See confirmation email"} small />
                            </div>
                            <div className="mt-8 text-sm text-navy/60 leading-relaxed">
                                <p>{confirmation.rate_kind === "flexible" ? "Free cancellation until 3 days before arrival." : "This rate is non-refundable."}</p>
                                <p className="mt-1">Questions? Call <a href="tel:+18766347439" className="text-navy hover:text-gold">+1 (876) 634-7439</a> or email <a href="mailto:stay@serenitycoveresorts.com" className="text-navy hover:text-gold">stay@serenitycoveresorts.com</a></p>
                            </div>
                            {confirmation.rate_kind === "resident" && customer && (
                                residentStatus?.verified ? (
                                    <div data-testid="post-book-verified" className="mt-8 rounded-2xl border-2 border-jade/50 bg-jade/5 p-6 text-left">
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-jade flex items-center gap-1.5">
                                            <span className="w-1.5 h-1.5 rounded-full bg-jade" /> Verified Resident · No document required
                                        </div>
                                        <p className="mt-2 text-navy font-medium">Your Verified Resident status is active</p>
                                        <p className="mt-1 text-[13px] text-navy/70">Because you were verified within the last {residentConfig?.verified_reuse_months || 3} months, this reservation is automatically confirmed at the resident rate — no re-upload needed. Please present your matching ID at check-in.</p>
                                    </div>
                                ) : (
                                    <div data-testid="post-book-verify" className="mt-8 rounded-2xl border-2 border-jade/50 bg-jade/5 p-6 text-left">
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-jade flex items-center gap-1.5">
                                            <span className="w-1.5 h-1.5 rounded-full bg-jade" /> Action required · Jamaican Resident Rate
                                        </div>
                                        <p className="mt-2 text-navy font-medium">Upload your verification document within {residentConfig?.deadline_hours || 24} hours</p>
                                        <p className="mt-1 text-[13px] text-navy/70">Your reservation is confirmed at the resident rate, subject to successful verification. If verification fails or the deadline passes, the booking reverts to the standard rate.</p>
                                        <Link
                                            to={`/account/verification/${confirmation.id}`}
                                            data-testid="post-book-verify-cta"
                                            className="mt-4 inline-flex items-center gap-2 bg-jade text-ivory px-6 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] hover:bg-jade/90"
                                        >
                                            Verify now
                                        </Link>
                                    </div>
                                )
                            )}
                            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                                <button
                                    type="button"
                                    data-testid="download-confirmation"
                                    onClick={() => window.print()}
                                    className="border border-navy text-navy px-6 py-3 rounded-full text-[11px] uppercase tracking-[0.16em] hover:bg-navy hover:text-ivory transition-colors"
                                >
                                    Download Confirmation
                                </button>
                                <Link to="/contact" className="border border-navy text-navy px-6 py-3 rounded-full text-[11px] uppercase tracking-[0.16em] hover:bg-navy hover:text-ivory transition-colors">Contact Resort</Link>
                                <button type="button" onClick={() => navigate("/")} className="btn-gold px-8 py-3.5 rounded-full">Return to Home</button>
                            </div>
                            {!customer && (
                                <div data-testid="post-book-signup" className="mt-8 border-t border-navy/10 pt-6">
                                    <p className="text-sm text-navy/70">Save this booking and start earning Cove Rewards points on future stays.</p>
                                    <Link
                                        to={`/signup`}
                                        className="mt-3 inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] hover:bg-gold/90 transition-colors"
                                    >
                                        Create an account with {confirmation.email}
                                    </Link>
                                </div>
                            )}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}

function Input({ label, value, onChange, type = "text", required, testId, textarea }) {
    const cls = "mt-1.5 w-full border border-navy/15 rounded-lg px-4 py-3 text-sm bg-white";
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">{label}</span>
            {textarea
                ? <textarea rows={3} data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className={cls} />
                : <input required={required} type={type} data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className={cls} />}
        </label>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-navy/50">{label}</div>
            <div className="mt-1 text-navy font-medium">{value}</div>
        </div>
    );
}

function ConfBox({ label, value, small }) {
    return (
        <div className="bg-ivory rounded-xl p-4 border border-navy/10">
            <div className="text-[10px] uppercase tracking-[0.22em] text-navy/50">{label}</div>
            <div className={`mt-1 ${small ? "text-xs" : "font-display text-lg"} text-navy`}>{value}</div>
        </div>
    );
}

function Summary({ room, selection, search, nights, promoDiscountPct, finalTotal }) {
    return (
        <aside className="lg:sticky lg:top-40 h-fit bg-white rounded-2xl border border-navy/10 p-6">
            <div className="relative aspect-[16/10] rounded-xl overflow-hidden">
                <RoomPreviewMedia image={room.hero_image} video={room.hero_video} alt={room.name} className="w-full h-full" />
            </div>
            <div className="mt-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{selection?.kind === "flexible" ? "Flexible Rate" : "Best Value"}</div>
                <h3 className="font-display text-xl text-navy mt-1">{room.name}</h3>
                <p className="text-xs text-navy/60 mt-1">{fmtDate(search.check_in)} → {fmtDate(search.check_out)} · {nights} night{nights === 1 ? "" : "s"}</p>
            </div>
            <div className="mt-4 pt-4 border-t border-navy/10 space-y-1.5 text-sm">
                <Row label={`Room subtotal`} value={usd(selection.rate.subtotal)} />
                <Row label="Resort fee" value={usd(selection.rate.resortFee)} />
                <Row label="Taxes" value={usd(selection.rate.taxes)} />
                {promoDiscountPct > 0 && <Row label={`Promo · ${search.promo_code.toUpperCase()}`} value={`-${Math.round(promoDiscountPct * 100)}%`} accent />}
                <div className="border-t border-navy/10 pt-2 mt-2 flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Final total</span>
                    <span className="font-display text-2xl text-gold">{usd2(finalTotal)}</span>
                </div>
            </div>
        </aside>
    );
}
